import {json, redirect} from '@remix-run/node'
import {
  Links,
  LiveReload,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useNavigation,
} from '@remix-run/react'
import {withSentry} from '@sentry/remix'
import mapboxStyle from 'mapbox-gl/dist/mapbox-gl.css'
import NProgress from 'nprogress'
import nProgressStyles from 'nprogress/nprogress.css'
import {useEffect} from 'react'
import calendarStyle from 'react-big-calendar/lib/css/react-big-calendar.css'
import reactDatePickerStyles from 'react-datepicker/dist/react-datepicker.css'
import {toast, Toaster} from 'react-hot-toast'
import {Notification} from '~/components/Notification'
import {commitSession, getUserSession} from '~/services/session.server'
import {resolveRedirect} from '~/services/redirects/resolve.server'
import {clientEnvVariablesSchema} from '~/types'
import styles from './app.css'
import {useAuthenticatedLoaderData} from './hooks'
import {initChameleon, setUpChameleon} from './utils/chameleon.client'
import {initPendo, setUpPendo} from './utils/pendo.client'
import {DJ_METRICS_ENABLED, DJ_METRICS_VENDORJS} from './constants'
import type {LinksFunction, LoaderArgs, V2_MetaFunction} from '@remix-run/node'

NProgress.configure({showSpinner: false})

export const links: LinksFunction = () => {
  return [
    {rel: 'preconnect', href: 'https://fonts.googleapis.com'},
    {
      rel: 'preconnect',
      href: 'https://fonts.gstatic.com',
      crossOrigin: 'anonymous',
    },
    {
      rel: 'stylesheet',
      href: 'https://fonts.googleapis.com/css2?family=Assistant:wght@300;400;500;700&family=Faustina:wght@400;600&display=swap',
    },
    {rel: 'stylesheet', href: reactDatePickerStyles},
    {rel: 'stylesheet', href: calendarStyle},
    {rel: 'stylesheet', href: nProgressStyles},
    {rel: 'stylesheet', href: mapboxStyle},
    {rel: 'stylesheet', href: styles},
    {rel: 'stylesheet', href: reactDatePickerStyles},
  ]
}

export const meta: V2_MetaFunction = () => {
  return [
    {
      title: 'Dragonfly Intelligence',
    },
  ]
}

function App() {
  const {ENV, toastMessage} = useLoaderData<typeof loader>()
  const navigation = useNavigation()
  const authenticatedLoaderData = useAuthenticatedLoaderData()
  const user = authenticatedLoaderData?.user
  const {CHAMELEON_APP_ID, PENDO_API_KEY, APP_ENV} = ENV

  useEffect(() => {
    if (toastMessage) {
      const {message, type} = toastMessage

      toast.custom((t) => (
        <Notification
          type={type}
          message={message}
          show={t.visible}
          onClose={() => toast.dismiss(t.id)}
        />
      ))
    }
  }, [toastMessage])

  useEffect(() => {
    if (navigation.state === 'idle') {
      NProgress.done()
    } else {
      NProgress.start()
    }
  }, [navigation.state])

  useEffect(() => {
    const timeout = setTimeout(() => {
      try {
        initChameleon(CHAMELEON_APP_ID)
      } catch (error) {
        console.error('Chameleon init failed:', error)
      }
    }, 100)

    return () => clearTimeout(timeout)
  }, [CHAMELEON_APP_ID])

  useEffect(() => {
    const timeout = setTimeout(() => {
      try {
        if (window.chmln && authenticatedLoaderData?.user) {
          setUpChameleon(authenticatedLoaderData.user)
        }
      } catch (error) {
        console.error('Chameleon setup failed:', error)
      }
    }, 100)

    return () => clearTimeout(timeout)
  }, [authenticatedLoaderData])

  useEffect(() => {
    if (!PENDO_API_KEY) {
      return
    }

    try {
      initPendo(PENDO_API_KEY)
      if (user) {
        setUpPendo(user)
      }
    } catch (error) {
      console.error('Pendo setup failed:', error)
    }
  }, [PENDO_API_KEY, user])

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <Meta />
        <Links />
        {DJ_METRICS_ENABLED &&
          APP_ENV !== 'production' &&
          DJ_METRICS_VENDORJS && <script src={DJ_METRICS_VENDORJS} async />}
      </head>
      <body className="bg-white print:bg-transparent">
        <Outlet />
        <script
          dangerouslySetInnerHTML={{
            __html: `window.ENV = ${JSON.stringify(ENV)}`,
          }}
        />
        <Toaster />
        <ScrollRestoration />
        <Scripts />
        <LiveReload />
      </body>
    </html>
  )
}

export default withSentry(App)

export {CatchBoundary} from '~/components/Error/CatchBoundary'
export {ErrorBoundary} from '~/components/ErrorBoundary'
